import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAsynApplicationStepData,
  fetchAsyncCarLeasingStepData,
  fetchAsyncCells,
  fetchAsyncDistricts,
  fetchAsyncSectors,
  fetchAsyncVillages,
  fetchAsynUserIdentification,
  fetchAsynUserinformation,
  getCarLeasingStepData,
  getUserIdentification,
  getUserInformation,
} from "../../redux/transactions/UntappedSlice";
import Location from "./Location";
import moment from "moment";

function RiderIdentification({ data = {}, onChangeHandleRinderInfo }) {


  const dispatch = useDispatch();
  const userInformation = useSelector(getUserInformation);
  const userIdentification = useSelector(getUserIdentification);
  const currentStepSavedData = useSelector(getCarLeasingStepData);
  const phoneNumber = useSelector((state) => state.untapped.phoneNumber);
  
  // Local state for branches
  const [branches, setBranches] = useState([]);

  // Default form data with all fields
  const defaultFormData = {
    phone: "",
    firstName: "",
    middleName: "",
    lastName: "",
    birthDate: "",
    idNumber: "",
    province: "",
    district: "",
    sector: "",
    cell: "",
    village: "",
    gender: "",
    branch: "",
    email: "",
  };

  const [formData, setFormData] = useState({ ...defaultFormData });

  // Fetch branches from API on component mount
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await fetch(
          "https://applicationbackend.jalikoi.rw/api/v2/application/branches"
        );
        if (response.ok) {
          const data = await response.json();
        
          setBranches(data.data);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
        setBranches([]);
      }
    };

    fetchBranches();
  }, []);

  // Fetch application step data when phone number is available
  useEffect(() => {
    if (phoneNumber && phoneNumber.length === 10) {
      dispatch(fetchAsyncCarLeasingStepData({ phone: phoneNumber, step: 1 }));
    }
  }, [phoneNumber, dispatch]);

  // Populate form from userInformation and userIdentification
  useEffect(() => {
    if (userInformation || userIdentification) {
      setFormData((prev) => ({
        ...prev,
        ...(userInformation?.firstname && { firstName: userInformation.firstname }),
        ...(userInformation?.middleName && { middleName: userInformation.middleName }),
        ...(userInformation?.lastname && { lastName: userInformation.lastname }),
        ...(userInformation?.dob && { birthDate: userInformation.dob }),
        ...(userIdentification?.nid && { idNumber: userIdentification.nid }),
      }));
    }
  }, [userInformation, userIdentification]);

  // Populate form from saved step data
  useEffect(() => {
    if (
      currentStepSavedData?.step_data &&
      Object.keys(currentStepSavedData.step_data).length > 0
    ) {
      const stepData = currentStepSavedData.step_data;
      
      setFormData((prev) => ({
        ...prev,
        phone: stepData.phone ?? prev.phone,
        firstName: stepData.first_name ?? prev.firstName,
        middleName: stepData.middle_name ?? prev.middleName,
        lastName: stepData.last_name ?? prev.lastName,
        birthDate: stepData.dob
          ? moment(stepData.dob).format("YYYY-MM-DD")
          : prev.birthDate,
        idNumber: stepData.nid ?? prev.idNumber,
        province: stepData.place_of_birth?.cells?.sector?.district?.province?.id ?? prev.province,
        district: stepData.place_of_birth?.cells?.sector?.district?.id ?? prev.district,
        sector: stepData.place_of_birth?.cells?.sector?.id ?? prev.sector,
        cell: stepData.place_of_birth?.cells?.id ?? prev.cell,
        village: stepData.residence?.village?.id ?? prev.village,
        gender: stepData.gender ?? prev.gender,
        branch: stepData.branch ?? prev.branch,
        email: stepData.email ?? prev.email,
      }));
    }
  }, [currentStepSavedData]);

  // Populate form from props data
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      setFormData((prev) => ({
        ...prev,
        phone: data.phone ?? prev.phone,
        firstName: data.firstName ?? prev.firstName,
        middleName: data.middleName ?? prev.middleName,
        lastName: data.lastName ?? prev.lastName,
        birthDate: data.birthDate ?? prev.birthDate,
        idNumber: data.idNumber ?? prev.idNumber,
        province: data.province ?? prev.province,
        district: data.district ?? prev.district,
        sector: data.sector ?? prev.sector,
        cell: data.cell ?? prev.cell,
        village: data.village ?? prev.village,
        gender: data.gender ?? prev.gender,
        branch: data.branch.branch_id ?? prev.branch,
        email: data.email ?? prev.email,
      }));
    }
  }, [data]);

  // Debounced form update callback
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (JSON.stringify(formData) !== JSON.stringify(data)) {
        onChangeHandleRinderInfo(formData);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [formData, data, onChangeHandleRinderInfo]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Update location data
  const updatedLocation = useCallback((location) => {
    setFormData((prevData) => ({
      ...prevData,
      ...location,
    }));
  }, []);

  // Fetch location data based on selections
  useEffect(() => {
    if (formData.province) {
      dispatch(fetchAsyncDistricts({ province: formData.province }));
    }
  }, [dispatch, formData.province]);

  useEffect(() => {
    if (formData.district) {
      dispatch(fetchAsyncSectors({ district: formData.district }));
    }
  }, [dispatch, formData.district]);

  useEffect(() => {
    if (formData.sector) {
      dispatch(fetchAsyncCells({ sector: formData.sector }));
    }
  }, [dispatch, formData.sector]);

  useEffect(() => {
    if (formData.cell) {
      dispatch(fetchAsyncVillages({ cell: formData.cell }));
    }
  }, [dispatch, formData.cell]);

  // Fetch user identification when phone number is complete
  useEffect(() => {
    if (formData.phone.length === 10) {
      dispatch(fetchAsynUserIdentification({ contact: formData.phone }));
      dispatch(fetchAsynUserinformation({ contact: formData.phone }));
    }
  }, [dispatch, formData.phone]);

  return (
    <form>
      {/* Phone and ID Number */}
      <div className="flex justify-center items-center gap-4 mb-4">
        <div className="w-1/2">
          <label className="block text-sm font-medium mb-1">
            Nimero ya telefone
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="078..."
            maxLength="10"
            className="w-full focus:outline-none px-3 py-2 border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
          />
        </div>
        <div className="w-1/2">
          <label className="block text-sm font-medium mb-1">
            Nimero y'irangamuntu
          </label>
          <input
            type="text"
            name="idNumber"
            value={formData.idNumber}
            onChange={handleChange}
            placeholder="Nimero y'irangamuntu"
            maxLength="16"
            className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
          />
        </div>
      </div>

      {/* Names */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">Izina ribanza</label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Izina ribanza"
            className="w-full px-3 focus:outline-none py-2 border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Izina ryo hagati</label>
          <input
            type="text"
            name="middleName"
            value={formData.middleName}
            onChange={handleChange}
            placeholder="Izina ryo hagati"
            className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Irindi zina</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Irindi zina"
            className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
          />
        </div>
      </div>

      {/* Birth Date and Gender */}
      <div className="flex w-full justify-between items-center gap-4 mb-4">
        <div className="w-2/5">
          <label className="block text-sm font-medium mb-1">
            Italiki y'amavuko
          </label>
          <input
            type="date"
            name="birthDate"
            value={formData.birthDate}
            max={new Date().toISOString().split("T")[0]}
            onChange={handleChange}
            className="w-full focus:outline-none px-3 py-2 border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
          />
        </div>
        <div className="w-3/5">
          <label className="block text-sm font-medium mb-1">Gender</label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
          >
            <option value="">Hitamo</option>
            <option value="MALE">Gabo</option>
            <option value="FEMALE">Gore</option>
            <option value="OTHER">Ikindi</option>
          </select>
        </div>
      </div>

      {/* Branch and Email */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">Branch</label>
          <select
            name="branch"
            value={formData.branch}
            onChange={handleChange}
            className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
          >
            <option value="">Hitamo</option>
            {branches && branches.length > 0 ? (
              branches.map((branch) => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </option>
              ))
            ) : (
              <>
                <option value="Kayonza">Kayonza</option>
                <option value="Kigali">Kigali</option>
              </>
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="example@email.com"
            className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
          />
        </div>
      </div>

      {/* Location */}
      <label className="block text-sm font-medium mb-2">
        Aho wavukiye / Aho irangamuntu yatangiwe
      </label>
      <Location
        province={formData.province}
        district={formData.district}
        sector={formData.sector}
        cell={formData.cell}
        village={formData.village}
        onLocationChange={updatedLocation}
      />
    </form>
  );
}

export default RiderIdentification;
